import api, { API_REPORT_TIMEOUT_MS } from "./api";

/** Paginated `{ results }` vs plain `[...]` from list endpoints */
export function unwrapListPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.results)) return payload.results;
  return [];
}

/**
 * Prefer catalog row as canonical, fill missing pct fields from `holdings[].project`
 * when both come from the same API (handles odd client/proxy payloads).
 */
export function enrichProjectsWithHoldingsProjectFields(projectRows, holdings) {
  const rows = unwrapListPayload(projectRows);
  const hold = unwrapListPayload(holdings);
  const byId = new Map(rows.map((p) => [p?.id, { ...p }]));
  const fillKeys = ["expected_return_pct", "estimated_return_pct", "expectedReturnPct", "estimatedReturnPct"];
  const fillMissing = (target, src) => {
    if (!target || !src) return;
    for (const key of fillKeys) {
      const v = src[key];
      const cur = target[key];
      if (v != null && v !== "" && (cur == null || cur === "")) {
        target[key] = v;
      }
    }
  };
  for (const h of hold) {
    const nested = h?.project;
    if (!nested?.id) continue;
    const slot = byId.get(nested.id);
    if (slot) fillMissing(slot, nested);
  }
  return rows.map((p) => {
    const id = p?.id;
    if (!id) return p;
    return byId.get(id) ?? p;
  });
}

export const listProjects = () => api.get("/projects/");
export const fetchProjectDetails = (projectId) => api.get(`/projects/${projectId}/`);
export const fetchPlatformSettings = () => api.get("/platform/settings/");
/** Transfer/redeem/purchase can run settlement checks; allow longer than default list reads. */
const INVESTMENT_MUTATION_TIMEOUT_MS = 60000;
const INVESTMENT_PREVIEW_TIMEOUT_MS = 30000;

export const purchaseShares = (payload) =>
  api.post("/investments/purchase/", payload, { timeout: INVESTMENT_MUTATION_TIMEOUT_MS });
export const requestShareTransfer = (payload) =>
  api.post("/investments/transfer/", payload, { timeout: INVESTMENT_MUTATION_TIMEOUT_MS });
export const fetchTransferPreview = (projectId, shares, toInvestorId) =>
  api.get("/investments/transfer-preview/", {
    params: {
      project_id: projectId,
      shares,
      ...(toInvestorId ? { to_investor_id: toInvestorId } : {}),
    },
    timeout: INVESTMENT_PREVIEW_TIMEOUT_MS,
  });
export const fetchIncomingTransfers = () => api.get("/investments/transfers/incoming/");
export const fetchOutgoingTransfers = () => api.get("/investments/transfers/outgoing/");
export const fetchReceiverTransferPreview = (txId) =>
  api.get(`/investments/transfer/${txId}/receiver-preview/`);
export const acceptIncomingTransfer = (txId, payload = {}) =>
  api.post(`/investments/transfer/${txId}/accept/`, payload);
export const rejectIncomingTransfer = (txId, payload) =>
  api.post(`/investments/transfer/${txId}/reject/`, payload);
export const cancelOutgoingTransfer = (txId) => api.post(`/investments/transfer/${txId}/cancel/`);
export const requestRedemption = (payload) =>
  api.post("/investments/redeem/", payload, { timeout: INVESTMENT_MUTATION_TIMEOUT_MS });
/** GET estimate: gross, fees, net (same rules as submit). assume_pending_days is accepted but ignored (no daily fee). */
export const fetchRedemptionPreview = (projectId, shares) =>
  api.get("/investments/redeem/preview/", {
    params: {
      project_id: projectId,
      shares,
    },
  });
export const fetchPortfolio = () => api.get("/investments/portfolio/");
export const fetchPurchaseHistory = (projectId) =>
  api.get("/investments/purchases/history/", { params: { project_id: projectId } });
/** Primary fundraise purchases + funded secondary transfers received (investor Buy View). */
export const fetchAcquisitionHistory = (projectId) =>
  api.get("/investments/acquisitions/history/", { params: { project_id: projectId } });
/** Paginated redemption ledger; pass filters (project_id, status, date_from, date_to, sort, q, page). */
export const fetchRedemptionHistory = (params = {}) => api.get("/investments/redemptions/history/", { params });

export function unwrapRedemptionHistoryPayload(data) {
  if (Array.isArray(data)) return { rows: data, summary: null, count: data.length };
  if (data && Array.isArray(data.results)) {
    return { rows: data.results, summary: data.summary || null, count: data.count ?? data.results.length };
  }
  return { rows: [], summary: null, count: 0 };
}

export const fetchRedemptionBalance = (projectId) =>
  api.get("/investments/redemptions/balance/", { params: projectId ? { project_id: projectId } : {} });

export const cancelRedemptionRequest = (id) => api.post(`/investments/redemptions/${id}/cancel/`);
/** Paginated transfer history; pass filters or `{ project_id }` for one project. */
export const fetchTransferHistory = (params = {}) => {
  const p = typeof params === "string" || typeof params === "number" ? { project_id: String(params) } : params;
  return api.get("/investments/transfers/history/", { params: p });
};

export function unwrapTransferHistoryPayload(data) {
  if (Array.isArray(data)) return { rows: data, summary: null, count: data.length };
  if (data && Array.isArray(data.results)) {
    return { rows: data.results, summary: data.summary || null, count: data.count ?? data.results.length };
  }
  return { rows: [], summary: null, count: 0 };
}

export const listAssetManagerProjects = () => api.get("/asset-manager/projects/");
export const submitAssetManagerPnl = (payload) => api.post("/asset-manager/pnl/", payload);
export const updateAssetManagerPnl = (id, payload) => api.patch(`/asset-manager/pnl/${id}/`, payload);
export const cancelAssetManagerPnl = (id) => api.post(`/asset-manager/pnl/${id}/cancel/`);
export const listAssetManagerPnl = (params = {}) => api.get("/asset-manager/pnl/list/", { params });
export const listAssetManagerTransactions = (params = {}) =>
  api.get("/asset-manager/history/transactions/", { params });
export const fetchAssetManagerCommissions = () => api.get("/asset-manager/history/commissions/");
export async function fetchPriceHistory(projectId) {
  const { data } = await api.get(`/investments/${projectId}/price-history/`);
  const rows = unwrapListPayload(data);
  return {
    data: [...rows].sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at)),
  };
}
export const getProjectOverviewPdfSignedUrl = (projectId) => api.get(`/projects/${projectId}/overview-pdf-signed/`);

export const fetchProjectLandingById = (projectId) => api.get(`/projects/${projectId}/landing/`);
export const fetchPublicProjectLanding = (slug) => api.get(`/public/projects/${encodeURIComponent(slug)}/landing/`);
export const fetchPublicProjectLandingDocumentSigned = (slug) =>
  api.get(`/public/projects/${encodeURIComponent(slug)}/documents/overview-signed/`);

export async function downloadProjectLandingPdfById(projectId, slugOrCode = "") {
  const res = await api.get(`/projects/${projectId}/landing.pdf`, {
    responseType: "blob",
    timeout: API_REPORT_TIMEOUT_MS,
  });
  const safe = String(slugOrCode || "project")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "") || "project";
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${safe}-landing.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
}

export async function downloadPublicProjectLandingPdf(slug) {
  const res = await api.get(`/public/projects/${encodeURIComponent(slug)}/landing.pdf`, {
    responseType: "blob",
    timeout: API_REPORT_TIMEOUT_MS,
  });
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${slug}-landing.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
}

export const fetchShareCertificateVerify = (token) =>
  api.get(`/share-certificates/verify/${encodeURIComponent(token)}/`);

export const fetchShareCertificatePreview = (projectId) =>
  api.get(`/investments/${projectId}/share-certificate/`);

export async function downloadSharesCertificatePdf(projectId, projectCode = "") {
  const res = await api.get(`/investments/${projectId}/share-certificate/pdf/`, { responseType: "blob" });
  const safeCode = String(projectCode || "project")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_-]/g, "") || "project";
  const blob = new Blob([res.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `share_certificate_${safeCode}.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
}
